import { Aircraft } from '@/domain/models/Aircraft'

import { isSet } from './validation'

export const calculateMinimumCruiseAltitude = (departureAltitude: number, destinationAltitude: number) => {
  const baseMinimum = Math.max(departureAltitude || 0, destinationAltitude || 0)
  return Math.max(baseMinimum + 500, 500)
}

export const calculateMaximumCruiseAltitude = (
  departureAltitude: number,
  destinationAltitude: number,
  aircraft: Aircraft,
  routeDistance: number
) => {
  const { operationalCeiling, rateAc, rateDc, speedAc, speedDc } = aircraft
  const maxAltitude = Math.floor(operationalCeiling / 500) * 500
  const minAltitude = Math.ceil(Math.max(departureAltitude || 0, destinationAltitude || 0) / 500) * 500

  for (let i = minAltitude; i <= maxAltitude; i += 500) {
    const timeToToc = (i - (departureAltitude || 0)) / 60 / rateAc
    const distanceToToc = timeToToc * speedAc

    const timeToTod = (i - (destinationAltitude || 0)) / 60 / rateDc
    const distanceToTod = timeToTod * speedDc

    if (distanceToToc + distanceToTod > routeDistance) {
      return i - 500
    }
  }

  return maxAltitude
}

/**
 * Calculate the ETE to the top of climb.
 *
 * @param currentAltitude in ft
 * @param cruiseAltitude in ft
 * @param rateAc in ft/min
 * @returns time in seconds
 */
export const calculateEteToTocInSeconds = (currentAltitude: number, cruiseAltitude: number, rateAc: number) => {
  if (currentAltitude >= cruiseAltitude) return 0

  const eteToTocInMinutes = (cruiseAltitude - currentAltitude) / rateAc

  return eteToTocInMinutes * 60
}

/**
 * Calculate the horizontal distance to the top of climb.
 *
 * @param eteToToc in seconds.
 * @param speedAc in knots.
 * @returns distance in nautical miles
 */
export const calculateDistanceToTocInNM = (eteToToc: number, speedAc: number) => {
  if (eteToToc === 0) return 0

  const distanceToToc = speedAc * (eteToToc / 3600)

  return distanceToToc
}

/**
 * Calculate the horizontal distance to the top of descent.
 *
 * @param currentAltitude in ft.
 * @param arvAltitude in ft.
 * @param rateDc in ft/min.
 * @param speedDc in knots.
 * @param routeDistance in nautical miles.
 * @returns distance in nautical miles
 */
export const calculateDistanceToTodInNM = (
  currentAltitude: number,
  arvAltitude: number,
  rateDc: number,
  speedDc: number,
  routeDistance: number
) => {
  if (currentAltitude <= arvAltitude) return 0

  const todToArvAltitudeDiff = currentAltitude - arvAltitude

  const timeToLandInHours = todToArvAltitudeDiff / rateDc / 60

  const distanceNeededToLand = speedDc * timeToLandInHours
  const distanceToTod = routeDistance - distanceNeededToLand

  if (distanceToTod < 0) return 0

  return distanceToTod
}

/**
 * Calculate the horizontal distance from the top of descent to the arrival.
 *
 * @param currentAltitude in ff.
 * @param arvAltitude in ft.
 * @param rateDc in ft/min.
 * @param speedDc in knots.
 * @returns distance in nautical miles
 */
export const calculateDistanceFromTodToArrivalInNM = (
  currentAltitude: number,
  arvAltitude: number,
  rateDc: number,
  speedDc: number
) => {
  if (currentAltitude <= arvAltitude) return 0

  const todToArvAltitudeDiff = currentAltitude - arvAltitude

  const timeToLandInHours = todToArvAltitudeDiff / rateDc / 60

  const distanceNeededToLand = speedDc * timeToLandInHours

  return distanceNeededToLand
}

/**
 * Calculate the ETE from the top of descent to the destination.
 *
 * @param currentAltitude in ft.
 * @param arvAltitude in ft.
 * @param rateDc in ft/min.
 * @returns time in seconds
 */
export const calculateEteFromTodToArrivalInSeconds = (currentAltitude: number, arvAltitude: number, rateDc: number) => {
  if (currentAltitude <= arvAltitude) return 0

  const todToArvAltitudeDiff = currentAltitude - arvAltitude

  const timeToLandInSeconds = (todToArvAltitudeDiff / rateDc) * 60

  return timeToLandInSeconds
}

/**
 * Calculate the ETE from the top of climb to the top of descent.
 *
 * @param cruiseDistance in nautical miles.
 * @param speedCruise in knots.
 * @returns time in seconds
 */
export const calculateEteFromTocToTodInSeconds = (cruiseDistance: number, speedCruise: number) => {
  if (cruiseDistance < 0) return 0

  const eteToTodInSeconds = (cruiseDistance / speedCruise) * 3600

  return eteToTodInSeconds
}

/**
 * Calculate the fuel consumption of the flight stage.
 *
 * @param timeInSeconds in seconds.
 * @param fuelConsumption in liters per hour.
 * @returns fuel consumption in liters per hour.
 */
export const calculateFuelConsumptionPerHour = (timeInSeconds: number, fuelConsumption: number) => {
  if (timeInSeconds <= 0) return 0

  const timeInHours = timeInSeconds / 3600
  const fuelConsumptionPerHour = timeInHours * fuelConsumption

  return fuelConsumptionPerHour
}

/**
 * Filter the cruise altitudes that are valid for VFR flights.
 *
 * @param currentAltitude in ft.
 * @returns a boolean
 */
export const filterByVfr = (cruiseAltitude: number) => {
  return cruiseAltitude <= 14500 && cruiseAltitude % 500 === 0 && cruiseAltitude % 1000 !== 0
}

/**
 * Filter the cruise altitudes that are valid for IFR flights.
 *
 * @param currentAltitude in ft.
 * @returns a boolean
 */
export const filterByIfr = (cruiseAltitude: number) => {
  return cruiseAltitude <= 61000 && cruiseAltitude % 1000 === 0
}

/**
 * Filter the cruise altitudes that are valid for even flight levels.
 * obs: even following the aviation rules.
 * @see https://sabordevoar.blogspot.com/2012/06/informacao-30-escolha-do-nivel-de-voo.html
 * @param cruiseAltitude in ft
 * @returns a boolean
 */
export const filterByEven = (cruiseAltitude: number) => {
  if (cruiseAltitude > 41000) {
    const valueToCheck = cruiseAltitude - 41000
    const timesToCheck = valueToCheck / 2000
    return timesToCheck % 2 !== 0
  } else {
    const digitsSliceIndex = cruiseAltitude < 10000 ? 1 : 2
    const cruiseAltitudeDigits = cruiseAltitude.toString().slice(0, digitsSliceIndex)

    return Number(cruiseAltitudeDigits) % 2 === 0
  }
}

/**
 * Filter the cruise altitudes that are valid for odd flight levels.
 * obs: odd following the aviation rules.
 * @see https://sabordevoar.blogspot.com/2012/06/informacao-30-escolha-do-nivel-de-voo.html
 * @param cruiseAltitude in ft
 * @returns a boolean
 */
export const filterByOdd = (cruiseAltitude: number) => {
  if (cruiseAltitude > 41000) {
    const valueToCheck = cruiseAltitude - 41000
    const timesToCheck = valueToCheck / 2000
    return timesToCheck % 2 === 0
  } else {
    const digitsSliceIndex = cruiseAltitude < 10000 ? 1 : 2
    const cruiseAltitudeDigits = cruiseAltitude.toString().slice(0, digitsSliceIndex)

    return Number(cruiseAltitudeDigits) % 2 !== 0
  }
}

/**
 * A function to simulate the flight stages and return the total values for time and fuel consumption.
 *
 * @param firstWaypointAltitude The first waypoint altitude in ft
 * @param lastWaypointAltitude The last waypoint altitude in ft
 * @param aircraft The aircraft instance
 * @param cruiseAltitude The cruise altitude in ft
 * @param routeTotalDistance The total distance of the route in nautical miles
 * @returns An object with the distanceToToc, distanceToTod, total time and fuel consumption
 */
export const simulateWaypointsInfo = (
  firstWaypointAltitude: number,
  lastWaypointAltitude: number,
  aircraft: Aircraft,
  cruiseAltitude: number,
  routeTotalDistance: number
) => {
  if (!isSet(firstWaypointAltitude) || !isSet(lastWaypointAltitude)) return
  if (!isSet(aircraft) || !isSet(cruiseAltitude)) return
  if (!isSet(routeTotalDistance)) return

  if (!isSet(firstWaypointAltitude) || !isSet(lastWaypointAltitude) || !isSet(routeTotalDistance)) return

  // On Climb calculations
  const timeToToc = calculateEteToTocInSeconds(firstWaypointAltitude, cruiseAltitude, aircraft.rateAc)

  const distanceToToc = calculateDistanceToTocInNM(timeToToc, aircraft.speedAc)
  const fuelConsumptionToToc = calculateFuelConsumptionPerHour(timeToToc, aircraft.fuelConsumptionAc)

  // On Cruise calculations
  const distanceToTod = calculateDistanceToTodInNM(
    cruiseAltitude,
    lastWaypointAltitude,
    aircraft.rateDc,
    aircraft.speedDc,
    routeTotalDistance
  )

  const timeOnCruise = calculateEteFromTocToTodInSeconds(distanceToTod - distanceToToc, aircraft.speedCruise)
  const fuelConsumptionOnCruise = calculateFuelConsumptionPerHour(timeOnCruise, aircraft.fuelConsumptionCruise)

  // On Descent calculations
  const distanceToLastWaypoint = routeTotalDistance - distanceToTod

  const timeOnDescent = (distanceToLastWaypoint / aircraft.speedDc) * 3600
  const fuelConsumptionOnDescent = calculateFuelConsumptionPerHour(timeOnDescent, aircraft.fuelConsumptionDc)

  // Total calculations
  const totalTimeOnRoute = timeToToc + timeOnCruise + timeOnDescent
  const totalFuelConsumption = fuelConsumptionToToc + fuelConsumptionOnCruise + fuelConsumptionOnDescent

  return {
    distanceToToc,
    distanceToTod,
    totalTimeOnRoute,
    totalFuelConsumption
  }
}
